import { Carregando, EstadoVazio } from '../../../common/interface';
import { quando } from '../../../../utils/formato';
import { moeda } from '../../formato';
import { useExtrato } from './useCreditos';
import type { LancamentoCredito } from './types';

/**
 * Entrada ou saída por tipo — direção confirmada em `creditos_ui.dart`
 * (research.md §1.8; o README de design tem a direção invertida):
 *
 *   CREDITO  → entrou
 *   ESTORNO  → voltou ("dinheiro que voltou não é dinheiro novo, mas segue
 *              sendo positivo")
 *   DEBITO   → saiu — mostra só o KM, nunca preço/km
 *   AJUSTE e desconhecido (RESERVA/LIBERACAO incluídos) → saída, com o texto
 *              literal "Movimentação" no desconhecido; nunca some da lista
 *
 * A seta desenhada saiu (0094): o sinal do valor e a cor de papel já dizem a
 * direção, e a seta sozinha não se lia.
 */
function entrou(tipo: string): boolean {
  return tipo === 'CREDITO' || tipo === 'ESTORNO';
}

function rotulo(item: LancamentoCredito): string {
  if (item.tipo === 'CREDITO' || item.tipo === 'DEBITO' || item.tipo === 'ESTORNO' || item.tipo === 'AJUSTE') {
    return item.descricao || item.tipo;
  }
  return 'Movimentação';
}

function mesDoLancamento(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase();
}

export function ExtratoLista() {
  const { lancamentos, carregando, erro } = useExtrato();

  if (carregando) return <Carregando rotulo="Carregando o extrato" />;
  if (erro) return <p className="co-erro" role="alert">{erro}</p>;
  if (lancamentos.length === 0) {
    return (
      <EstadoVazio titulo="Nenhuma movimentação por enquanto">
        Suas recargas e emissões aparecem aqui.
      </EstadoVazio>
    );
  }

  const grupos = new Map<string, LancamentoCredito[]>();
  for (const item of lancamentos) {
    const chave = mesDoLancamento(item.dtLancamento);
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave)!.push(item);
  }

  return (
    <div>
      {Array.from(grupos.entries()).map(([mes, itens]) => (
        <section key={mes} className="co-cartao co-extrato-mes" aria-label={mes}>
          <h3 className="co-rotulo">{mes}</h3>
          {itens.map(item => {
            const positivo = entrou(item.tipo);
            return (
              <div key={item.id} className="co-lancamento">
                <div className="co-lancamento-texto">
                  <div className="co-lista-curta-titulo">{rotulo(item)}</div>
                  <div className="co-lista-curta-apoio">
                    {quando(item.dtLancamento)}
                    {item.tipo === 'DEBITO' && item.kmCobrado !== null && ` · ${item.kmCobrado} km`}
                  </div>
                </div>
                <div>
                  <div className={`co-lancamento-valor ${positivo ? 'co-lancamento-entrada' : 'co-lancamento-saida'}`}>
                    {positivo ? '+' : '−'}{moeda(Math.abs(item.valor))}
                  </div>
                  <div className="co-lista-curta-apoio">Saldo {moeda(item.saldoApos)}</div>
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
